// The lock signal of the hard close (rev 4, DESIGN §2.3, §6): the one rule
// every board locks by, on a hard round only.
//
// A hard round's board locks at the FIRST of
//   1. `race_starting`'s arrival, and
//   2. its own countdown: heardAt(`race_ready`) + (W − X), on `performance.now()`,
// empty or not, and sends every option at once. Nothing here reads a device
// clock against a server stamp: both signals are moments of arrival on this
// page. The lock is UX: Go judges every bet against K on its own clock, so a
// board that locks late only sends late (refused before money) and one that
// locks early only gives its player less time to choose.
//
// The rule is a small machine with injected clocks (`createBetLock`), so the
// boards' hook (`hooks/useBetLock.ts`) and the timing tests drive the same
// code; and two pure readings (`betLockMoments`, `stageTwoAt`) for anything
// that only draws the countdown.
import { isFrozenSubmission } from 'src/components/ChipPlacementPanel/submissionLifecycle'
import type { HardClose } from 'src/types/domain/HardClose'

export type LockSignal = 'race_starting' | 'countdown'

/** How long before the board's estimate of K a send still waits for its channel's join. */
export const JOIN_WAIT_MARGIN_MS = 500

export interface BetLockFired {
  signal: LockSignal
  /** `performance.now()` ms at which the board locked. */
  at: number
  /**
   * The countdown fired far past its moment: a frozen or backgrounded tab
   * (today's frozen-tab check, on the countdown timer). The board then holds
   * its chips for the next round. Never true on `race_starting`'s arrival.
   */
  frozen: boolean
}

export interface BetLock {
  /** `race_starting` arrived for this round: lock now, unless the countdown did first. */
  starting(): void
  /** The round is gone (or the board unmounted): the countdown never fires. */
  dispose(): void
  /** The lock, once it has fired; null before. */
  fired(): BetLockFired | null
}

const perfNow = (): number =>
  typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now()

/**
 * One round's lock. Fires `onLock` exactly once, at the first of the two
 * signals; a second signal, a disposed machine and a re-armed timer fire
 * nothing. `lockAt` undefined: no countdown (a board that did not hear
 * `race_ready` live), so only `race_starting` locks it.
 */
export function createBetLock(input: {
  lockAt: number | undefined
  onLock(fired: BetLockFired): void
  now?: () => number
  wallNow?: () => number
}): BetLock {
  const now = input.now ?? perfNow
  const wallNow = input.wallNow ?? Date.now
  let done: BetLockFired | null = null
  let disposed = false
  let timer: ReturnType<typeof setTimeout> | null = null

  function fire(signal: LockSignal, frozen: boolean): void {
    if (done !== null || disposed) return
    if (timer !== null) {
      clearTimeout(timer)
      timer = null
    }
    done = { signal, at: now(), frozen }
    input.onLock(done)
  }

  if (input.lockAt !== undefined && Number.isFinite(input.lockAt)) {
    const delayMs = Math.max(0, input.lockAt - now())
    const armedWallAt = wallNow()
    timer = setTimeout(() => {
      timer = null
      fire(
        'countdown',
        isFrozenSubmission({ timerArmedAt: armedWallAt, timerDelayMs: delayMs, now: wallNow() })
      )
    }, delayMs)
  }

  return {
    starting: () => fire('race_starting', false),
    dispose() {
      disposed = true
      if (timer !== null) {
        clearTimeout(timer)
        timer = null
      }
    },
    fired: () => done
  }
}

/** The countdown's end: heardAt(`race_ready`) + (W − X). Undefined without a heard row or a usable W. */
export function countdownLockAt(
  readyHeardAt: number | undefined,
  close: HardClose
): number | undefined {
  if (readyHeardAt === undefined || close.windowS === null) return undefined
  return readyHeardAt + (close.windowS - close.lockLeadS) * 1000
}

export interface BetLockMoments {
  /** The countdown's end, `performance.now()` ms. Undefined: this board has no countdown. */
  lockAt?: number
  /** When the board locked, once it has: the first of `race_starting`'s arrival and `lockAt`. */
  lockedAt?: number
  /**
   * What locked it. `joined`: this page came in after `race_starting` (it read
   * the row, never heard it), so the round is closed to it and nothing is sent.
   */
  lockedBy?: LockSignal | 'joined'
}

/**
 * The lock as anything that only DRAWS it reads it (the ring, the toolbar, a
 * drain): the same two signals as the machine, without its timer. A display
 * ticks; the board's own hook fires on the moment.
 */
export function betLockMoments(input: {
  close: HardClose
  readyHeardAt: number | undefined
  startingHeardAt: number | undefined
  /** `race_starting` is among the round's rows, heard or read. */
  startingPresent: boolean
  now: number
}): BetLockMoments {
  const lockAt = countdownLockAt(input.readyHeardAt, input.close)
  const at = lockAt === undefined ? {} : { lockAt }
  if (input.startingHeardAt !== undefined) {
    if (lockAt !== undefined && lockAt <= input.startingHeardAt) {
      return { ...at, lockedAt: lockAt, lockedBy: 'countdown' }
    }
    return { ...at, lockedAt: input.startingHeardAt, lockedBy: 'race_starting' }
  }
  if (lockAt !== undefined && input.now >= lockAt) {
    return { ...at, lockedAt: lockAt, lockedBy: 'countdown' }
  }
  if (input.startingPresent) return { ...at, lockedBy: 'joined' }
  return at
}

/**
 * The two moments stage 2 is drawn by, from the lock: the send waits for its
 * channel's join until K − 0.5 s (past that it sends nothing and the chips
 * carry), and an option still open at the board's estimate of K reads
 * "confirming with your casino".
 */
export function stageTwoAt(
  lockedAt: number,
  lockLeadS: number
): { joinDeadline: number; confirmingAt: number } {
  return {
    joinDeadline: lockedAt + lockLeadS * 1000 - JOIN_WAIT_MARGIN_MS,
    confirmingAt: lockedAt + lockLeadS * 1000
  }
}
